import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Elapsed from 'components/common/Elapsed/Elapsed';
import moment from 'moment';
import React from 'react';
import { useGetRunningJobsQuery } from 'store/app/entities/BatchSlice';
import { I18n } from 'utils/i18n';
// Helper to safely extract batchConfigId value (handles both string and object)
const getBatchConfigIdLabel = batchConfigId => {
  if (!batchConfigId) return null;
  if (typeof batchConfigId === 'string') return batchConfigId;
  return batchConfigId._id || batchConfigId.name || 'Batch Config';
};

const RunningJobsView = () => {
  const { data: runningData, isLoading } = useGetRunningJobsQuery();

  const running = runningData?.running || [];
  const queueStats = runningData?.queueStats || {};

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h5" sx={{ marginBottom: 2 }}>
          {I18n.t('AdminBatchScene.CurrentlyRunning') || 'Currently Running Jobs'}
        </Typography>
        {running.length === 0 ? (
          <Typography color="textSecondary">
            {I18n.t('AdminBatchScene.NoRunningJobs') || 'No jobs are currently running.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>{I18n.t('AdminBatchScene.JobName') || 'Job Name'}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{I18n.t('AdminBatchScene.StartedAt') || 'Started At'}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{I18n.t('AdminBatchScene.Duration') || 'Duration'}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{I18n.t('AdminBatchScene.WorkerId') || 'Worker ID'}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{I18n.t('AdminBatchScene.BatchConfig') || 'Batch Config'}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{I18n.t('AdminBatchScene.Status') || 'Status'}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {running.map(job => {
                  return (
                    <TableRow key={job.executionId} hover>
                      <TableCell>{job.jobName}</TableCell>
                      <TableCell>{moment(job.startedAt).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Elapsed startedAt={job.startedAt} />
                          <CircularProgress size={16} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {job.workerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {job.batchConfigId ? (
                          <Chip
                            label={getBatchConfigIdLabel(job.batchConfigId)}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label="Running" color="primary" size="small" variant="soft" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h5" sx={{ marginBottom: 2 }}>
          {I18n.t('AdminBatchScene.QueueStats') || 'Queue Statistics'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {I18n.t('AdminBatchScene.TotalQueued') || 'Total Queued'}
              </Typography>
              <Typography variant="h4">{queueStats.totalQueued || 0}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {I18n.t('AdminBatchScene.TotalWorking') || 'Total Working'}
              </Typography>
              <Typography variant="h4">{queueStats.totalWorking || 0}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">
                {I18n.t('AdminBatchScene.ActiveWorkers') || 'Active Workers'}
              </Typography>
              <Typography variant="h4">{queueStats.workers?.length || 0}</Typography>
            </CardContent>
          </Card>
        </Box>
        {queueStats.queued && Object.keys(queueStats.queued).length > 0 && (
          <Box sx={{ marginTop: 2 }}>
            <Typography variant="h6" sx={{ marginBottom: 1 }}>
              {I18n.t('AdminBatchScene.QueuedByQueue') || 'Queued by Queue'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(queueStats.queued).map(([queueName, count]) => (
                <Chip key={queueName} label={`${queueName}: ${count}`} variant="outlined" />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RunningJobsView;
